/**
 * IANA timezone helpers for time-series datasets. The API only accepts
 * canonical IANA names, case-sensitively (`asia/tokyo`, `UTC+9` and `JST` are
 * all rejected), so pickers are sourced from the browser's own tz database.
 */

// For engines predating Intl.supportedValuesOf.
const FALLBACK_TIMEZONES = [
  "UTC",
  "Africa/Cairo",
  "Africa/Lagos",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
  "America/Sao_Paulo",
  "Asia/Dhaka",
  "Asia/Dubai",
  "Asia/Hong_Kong",
  "Asia/Jakarta",
  "Asia/Kolkata",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Europe/Berlin",
  "Europe/London",
  "Europe/Moscow",
  "Europe/Paris",
  "Pacific/Auckland",
];

let cachedTimezones = null;

export function getSupportedTimezones() {
  if (cachedTimezones) return cachedTimezones;

  let zones;
  try {
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    zones = null;
  }
  if (!Array.isArray(zones) || zones.length === 0) {
    zones = FALLBACK_TIMEZONES;
  }
  cachedTimezones = zones.includes("UTC") ? zones : ["UTC", ...zones];
  return cachedTimezones;
}

export function isValidTimezone(timezone) {
  return typeof timezone === "string" && timezone !== "" && getSupportedTimezones().includes(timezone);
}

export function guessTimezone() {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(timezone) ? timezone : "UTC";
  } catch {
    return "UTC";
  }
}

/** Current UTC offset of a zone, e.g. "+09:00". */
export function getTimezoneOffsetLabel(timezone, at = new Date()) {
  if (!isValidTimezone(timezone)) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      timeZoneName: "longOffset",
    }).formatToParts(at);
    const name = parts.find(part => part.type === "timeZoneName")?.value ?? "";
    // Chrome renders UTC itself as "GMT", not "GMT+00:00".
    return name.replace(/^GMT$/, "+00:00").replace(/^GMT/, "");
  } catch {
    return "";
  }
}

let cachedPickerItems = null;

// Memoised: building the list constructs one Intl formatter per zone.
export function getTimezonePickerItems() {
  if (cachedPickerItems) return cachedPickerItems;
  cachedPickerItems = getSupportedTimezones().map(timezone => {
    const offset = getTimezoneOffsetLabel(timezone);
    return {
      value: timezone,
      text: offset ? `${timezone} (UTC${offset})` : timezone,
    };
  });
  return cachedPickerItems;
}

// `datetime_fields_tz` wins: when both are present the legacy
// `datetime_fields` list is the stale one.
export function readDatetimeFields(dataset) {
  const fieldsTz = dataset?.datetime_fields_tz;
  if (Array.isArray(fieldsTz) && fieldsTz.length > 0) {
    return fieldsTz.map(field => ({
      name: field?.name ?? "",
      timezone: field?.timezone ?? null,
    }));
  }
  const legacyFields = dataset?.datetime_fields;
  if (!Array.isArray(legacyFields)) return [];
  return legacyFields.map(name => ({ name, timezone: null }));
}

export function readDatetimeFieldNames(dataset) {
  return readDatetimeFields(dataset).map(field => field.name);
}

export function effectiveTimezone(field, dataset) {
  return field?.timezone ?? dataset?.master_timezone ?? null;
}

// `replace` keeps the wall clock, `convert` keeps the instant, null stores the
// value as it arrived.
export const NAIVE_POLICY_REPLACE = "replace";
export const NAIVE_POLICY_CONVERT = "convert";
export const AWARE_POLICY_CONVERT = "convert";
export const AWARE_POLICY_REPLACE = "replace";

/** UI-only sentinel for "store as written"; the API takes null. */
export const POLICY_AS_IS = "as_is";

export function readTimezonePolicies(dataset) {
  return {
    naivePolicy: dataset?.naive_policy ?? null,
    awarePolicy: dataset?.aware_policy ?? null,
  };
}

export function buildDatetimeFieldsTz(names, timezoneByName = {}) {
  if (!Array.isArray(names)) return [];
  return names.map(name => ({
    name,
    timezone: timezoneByName[name] || null,
  }));
}

// Returns names and flags rather than messages, so callers can translate.
export function inspectDatetimeFieldsTz({
  fields = [],
  masterTimezone = null,
  primaryDatetimeField = null,
  naivePolicy = null,
  awarePolicy = null,
} = {}) {
  const seen = new Set();
  const seenLower = new Set();
  const duplicateNames = [];
  const caseCollisions = [];
  const missingTimezone = [];
  const invalidTimezones = [];
  let hasEmptyName = false;

  fields.forEach(field => {
    const name = field?.name ?? "";
    if (!name.trim()) {
      hasEmptyName = true;
      return;
    }
    if (seen.has(name)) {
      if (!duplicateNames.includes(name)) duplicateNames.push(name);
    } else if (seenLower.has(name.toLowerCase())) {
      if (!caseCollisions.includes(name)) caseCollisions.push(name);
    }
    seen.add(name);
    seenLower.add(name.toLowerCase());

    if (field?.timezone) {
      if (!isValidTimezone(field.timezone)) invalidTimezones.push(name);
    } else if (!masterTimezone) {
      missingTimezone.push(name);
    }
  });

  // The API rejects a timezone with no policy and a policy with no timezone,
  // so a missing timezone is only an error once a policy is set.
  const hasAnyTimezone = !!masterTimezone || fields.some(field => !!field?.timezone);
  const hasAnyPolicy = !!naivePolicy || !!awarePolicy;

  return {
    isEmpty: fields.length === 0,
    hasEmptyName,
    duplicateNames,
    caseCollisions,
    missingTimezone,
    invalidTimezones,
    invalidMasterTimezone: !!masterTimezone && !isValidTimezone(masterTimezone),
    primaryNotInFields: !!primaryDatetimeField && !seen.has(primaryDatetimeField),
    timezoneWithoutPolicy: hasAnyTimezone && !hasAnyPolicy,
    policyWithoutTimezone: hasAnyPolicy ? missingTimezone : [],
    policyWithoutAnyTimezone: hasAnyPolicy && !hasAnyTimezone,
  };
}

export function parseMissingColumnsFromMessage(message) {
  const text = String(message ?? "");
  const match = /Missing for:\s*([^.]+)/.exec(text);
  if (!match) {
    // Named missing columns but not in the shape we parse — the backend wording drifted.
    if (/\bmissing\b/i.test(text)) {
      console.warn("[timezone] unparsed missing-columns message:", text);
    }
    return [];
  }
  return match[1]
    .split(",")
    .map(name => name.trim())
    .filter(Boolean);
}

// The all-or-nothing rule (`GPTDatasetTsCreateSchema.check_timezone_policy_agreement` /
// `check_primary_field_in_datetime_fields` in maira-api) is checked against the merged
// dataset on update and raised as a plain-string `HTTPException.detail`, not FastAPI's
// 422 array — but the router's own generic `except Exception` handler re-wraps that same
// string into the identical `{response, error}` shape used for auth failures and any
// other unrelated exception (both also 400/422). Status/shape can't tell them apart, so
// this matches on the fixed vocabulary those validators actually raise; anything else is
// treated as a non-ts-validation error and left to the caller's generic handling.
const KNOWN_TS_VALIDATION_PATTERNS = [
  /must not contain duplicate/i,
  /must be one of/i,
  /invalid iana timezone/i,
  /missing for:/i,
  /would never be applied/i,
];

function isKnownTsValidationMessage(msg) {
  return KNOWN_TS_VALIDATION_PATTERNS.some(pattern => pattern.test(msg));
}

/**
 * Split the error body into per-field and form-level messages. Schema failures
 * give FastAPI's 422 array, keyed by index within `datetime_fields_tz`; the
 * all-or-nothing rule is checked against the merged dataset and 400s instead.
 */
export function parseDatasetTsValidationError(error) {
  const result = { master: null, columns: {}, fields: {}, form: [], missingColumns: [] };
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    detail.forEach(item => {
      const msg = String(item?.msg ?? "").replace(/^Value error,\s*/, "");
      if (!msg) return;
      const loc = Array.isArray(item?.loc) ? item.loc : [];

      if (loc[1] === "master_timezone") {
        result.master = msg;
      } else if (loc[1] === "datetime_fields_tz" && typeof loc[2] === "number") {
        result.columns[loc[2]] = msg;
      } else if (typeof loc[1] === "string") {
        result.fields[loc[1]] = msg;
      } else {
        result.form.push(msg);
      }
    });
  } else {
    const msg = typeof detail === "string" ? detail : (detail?.error ?? detail?.response ?? detail?.message);
    if (typeof msg === "string" && isKnownTsValidationMessage(msg)) {
      result.form.push(msg);
    }
  }

  result.form.forEach(msg => {
    parseMissingColumnsFromMessage(msg).forEach(name => {
      if (!result.missingColumns.includes(name)) result.missingColumns.push(name);
    });
  });

  return result;
}

export function hasDatasetTsValidationError(error) {
  const parsed = parseDatasetTsValidationError(error);
  return (
    !!parsed.master ||
    Object.keys(parsed.columns).length > 0 ||
    Object.keys(parsed.fields).length > 0 ||
    parsed.form.length > 0
  );
}

// Backend field name -> the locale path of the label the form shows for it, so
// an error can name the field the user actually sees.
const FIELD_LABEL_KEYS = {
  name: "$vuetify.gpt_dataset.dataset_name",
  description: "$vuetify.gpt_dataset.description_label_txt",
  tags: "$vuetify.gpt_profile.dataset_tags",
  idx_column_name: "$vuetify.gpt_dataset.index_column_name",
  primary_datetime_field: "$vuetify.gpt_dataset.primary_datetime_column",
  datetime_fields_tz: "$vuetify.gpt_dataset.datetime_columns",
};

export function datasetFieldLabelKey(field) {
  return FIELD_LABEL_KEYS[field] ?? null;
}

// Present when the update kicked off a rewrite of already-stored values.
export function readBackfillTaskId(response) {
  return response?.data?.detail?.timezone_backfill_task_id ?? null;
}

// `detail.task_id` is what the backend's timezone-backfill lock conflict sends (see `update_gpt_dataset_ts_data` in maira-api); status alone isn't enough since a 409 on this endpoint could later mean something else.
export function isBackfillConflict(error) {
  return error?.response?.status === 409 && !!error?.response?.data?.detail?.task_id;
}

// Naive/aware policy notice lines shown before ingestion; shared by the dataset-creation form and the document-upload modal since both apply the same stored policies to the values being ingested.
export function policyNoticeLines({ t, naivePolicy, awarePolicy, mappings }) {
  const lines = [];

  if (naivePolicy === NAIVE_POLICY_CONVERT) {
    if (mappings.length > 0) lines.push(`${t("naive_timestamp_convert_notice")} ${mappings.join(", ")}.`);
  } else if (naivePolicy === NAIVE_POLICY_REPLACE) {
    if (mappings.length > 0) lines.push(`${t("naive_timestamp_replace_notice")} ${mappings.join(", ")}.`);
  } else {
    lines.push(t("naive_timestamp_as_is_notice"));
  }

  if (awarePolicy === AWARE_POLICY_CONVERT) {
    lines.push(t("aware_timestamp_convert_notice"));
  } else if (awarePolicy === AWARE_POLICY_REPLACE) {
    lines.push(t("aware_timestamp_replace_notice"));
  } else {
    lines.push(t("aware_timestamp_as_is_notice"));
  }

  lines.push(t("mixed_column_notice"));
  return lines;
}
